import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Category } from '@/types/api'
import React, { useEffect, useState } from 'react'

interface BulkCategoryDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  selectedProducts: string[]
  categories: Category[]
  onBulkCategoryChange: (_categoryId: string, _productIds: string[]) => Promise<void>
  isLoading?: boolean
}

export function BulkCategoryDialog({
  open,
  onOpenChange,
  selectedProducts,
  categories,
  onBulkCategoryChange,
  isLoading = false,
}: BulkCategoryDialogProps) {
  const [categoryId, setCategoryId] = useState<string>('')

  useEffect(() => {
    if (!open) {
      setCategoryId('')
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    await onBulkCategoryChange(categoryId, selectedProducts)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Change Category</DialogTitle>
          <DialogDescription>
            Change category for {selectedProducts.length} selected product
            {selectedProducts.length > 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="category" className="text-right">
                New Category
              </label>
              <Select value={categoryId} onValueChange={setCategoryId} required>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!categoryId || isLoading}>
              {isLoading
                ? 'Processing...'
                : `Change category for ${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
